/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox'
import SoftButton from 'components/SoftButton'
import { memo, useRef, useEffect, useCallback, useState } from 'react'
import SoftDropzoneRoot from 'components/SoftDropzone/SoftDropzoneRoot'
import Dropzone from 'dropzone'
import { toast } from 'react-toastify'
import { IntakeContext } from 'context/IntakeContext'
import { useContext } from 'react'
import { largeFileUpload, MAX_ALLOWED_FILESIZE } from '../../utils/uploadHelper'
import { callZapier } from 'utils/zapier/callZapier'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ocrImage from 'assets/images/ocr-upload.svg'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
    position: absolute;
    top: 10px;
    left: 0px;
  }
`

export const Uploader = memo(({ options, cacheBuster = () => null }) => {
  const dropzoneRef = useRef()
  const dropzone = useRef()
  const { assumedUser, isAdmin, intakeState } = useContext(IntakeContext)
  const [processing, setProcessing] = useState(false)

  const [open, setOpen] = useState(false)
  const handleClose = () => {
    setOpen(false)
  }

  const userUploadedDocumentZapier = useCallback(() => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/3fxqiol/',
      payload: {
        user: {
          firstName: intakeState.firstName,
          lastName: intakeState.lastName,
          email: intakeState.email,
        },
      },
      sendNotification: false,
    })
  }, [intakeState])

  const handleSubmit = useCallback(async () => {
    const myDropzone = dropzone.current
    const acceptedFiles = myDropzone.getAcceptedFiles()
    console.log(myDropzone)
    setProcessing(true)
    const s3UploadPromises = acceptedFiles.map(async (file) => {
      return largeFileUpload(file, isAdmin, assumedUser)
    })
    // order is important - we want the records to be created before the uploads and don't want the uploads to be created
    // if the record creation is unsuccessful
    // await Promise.all(documentRecordPromises)
    await Promise.all(s3UploadPromises)
    myDropzone.processQueue()
    setProcessing(false)
    window.location.search === '?onboardingStep=paystubOCR' && setOpen(true)
    window.location.search !== '?onboardingStep=paystubOCR' &&
      toast.success('You have successfully uploaded your document(s)', {
        position: 'top-center',
        autoClose: 5000,
        pauseOnFocusLoss: false,
      })
    // TODO: add the notification "growler"/popup component for your confirmation INSIDE of the set timeout
    setTimeout(() => {
      myDropzone.removeAllFiles()
      cacheBuster()
    }, 1000)
    userUploadedDocumentZapier()
  }, [assumedUser, cacheBuster, isAdmin, userUploadedDocumentZapier])

  useEffect(() => {
    Dropzone.autoDiscover = false

    let uploadCount = 0
    function createDropzone() {
      // url is a stubbed function because we're handling submit ourselves
      const dropzoneInstance = new Dropzone(dropzoneRef.current, {
        url: '/',
        autoProcessQueue: true,
        addRemoveLinks: true,
        maxFilesize: MAX_ALLOWED_FILESIZE,
        dictDefaultMessage: `<b>Drag & Drop or Click Here!</b>`,
        timeout: 1,
        ...options,
      })
      dropzoneInstance.on('complete', () => {
        uploadCount++
        if (uploadCount === dropzoneInstance.getAcceptedFiles().length) {
          handleSubmit()
        }
      })
      return dropzoneInstance
    }

    function removeDropzone() {
      if (Dropzone.instances.length > 0)
        Dropzone.instances.forEach((dz) => dz.destroy())
    }
    dropzone.current = createDropzone()
    return () => removeDropzone()
  }, [options, handleSubmit])

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth="true"
        sx={{
          '.MuiPaper-root': {
            padding: '24px',
            borderRadius: '8px',
          },
        }}
      >
        <img
          src={ocrImage}
          style={{ maxWidth: '400px', display: 'block', margin: 'auto' }}
        />
        <DialogTitle
          sx={{ fontSize: '24px', paddingBottom: '8px' }}
          id="alert-dialog-title"
          textAlign="center"
        >
          {'Paystub Scanned Successfully!'}
        </DialogTitle>
        <DialogContent>
          {/* add in: We were able to pre-fill and skip 24 questions for you! with logic */}
          <Typography
            variant="body2"
            textAlign="center"
            id="alert-dialog-description"
          >
            Your paystub has been uploaded and relevant fields are being
            processed. Your uploaded paystubs can be found in the documents
            section of your profile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            variant="contained"
            sx={[
              {
                fontSize: '15px',
                padding: '10px 24px 9px',
                backgroundColor: '#344767',
                color: '#fff',
                fontWeight: 'normal',
                boxShadow: 'none',
                textTransform: 'none',
              },
              {
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#293852',
                  boxShadow: 'none',
                },
              },
            ]}
            onClick={handleClose}
            autoFocus
          >
            Continue with Onboarding
          </Button>
        </DialogActions>
      </Dialog>
      {processing && (
        <SoftBox>
          <SoftBox mt={2} textAlign="center">
            <Typography
              display="inline-block"
              sx={{ position: 'relative', height: '30px', width: '30px' }}
            >
              <SavingIcon fontSize="medium" className={'loading'} />
            </Typography>
            <Typography display="inline-block">Processing Upload...</Typography>
          </SoftBox>
        </SoftBox>
      )}
      <SoftBox>
        <SoftBox mt={2}>
          <SoftDropzoneRoot
            component="form"
            ref={dropzoneRef}
            className="form-control dropzone"
            onSubmit={handleSubmit}
            sx={{ backgroundColor: '#eee' }}
          >
            <SoftBox className="fallback">
              <SoftBox component="input" name="file" type="file" multiple />
            </SoftBox>
          </SoftDropzoneRoot>
        </SoftBox>
      </SoftBox>
      {/* <SoftBox pt={3} display="flex" justifyContent="space-between">
        <SoftBox>
          <Button
            variant="text"
            size="large"
            sx={[
              {
                color: '#344767',
                fontSize: '15px',
                padding: '10px 24px 9px',
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
              {
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  color: '#344767',
                  transform: 'none',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              },
              { '>span': { marginBottom: '2px' } },
            ]}
          >
            Cancel
          </Button>
        </SoftBox>
        <SoftBox>
          <Button
            variant="contained"
            sx={[
              {
                fontSize: '15px',
                padding: '10px 24px 9px',
                backgroundColor: '#344767',
                color: '#fff',
                fontWeight: 'normal',
                boxShadow: 'none',
              },
              {
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#293852',
                  boxShadow: 'none',
                },
              },
            ]}
            onClick={handleSubmit}
          >
            Upload Document(s)
          </Button>
        </SoftBox>
      </SoftBox> */}
    </>
  )
})
Uploader.displayName = 'Uploader'
